import { Ban, CheckCircle, Clock } from 'lucide-react';
import clsx from 'clsx';
import { libellePlaces, type NiveauPlaces, type SessionPlaces } from '../../lib/places';

/*
  Texte + couleur + icône : le niveau ne repose jamais sur la couleur seule
  (daltonisme), et la jauge porte une phrase complète pour les lecteurs d'écran.
*/
const STYLES: Record<NiveauPlaces, { texte: string; jauge: string; badge: string }> = {
  disponible: { texte: 'text-green-700', jauge: 'bg-green-500', badge: 'bg-green-600' },
  'presque-complet': { texte: 'text-amber-700', jauge: 'bg-amber-500', badge: 'bg-amber-500' },
  complet: { texte: 'text-red-700', jauge: 'bg-red-500', badge: 'bg-red-500' },
};

const ICONES: Record<NiveauPlaces, typeof CheckCircle> = {
  disponible: CheckCircle,
  'presque-complet': Clock,
  complet: Ban,
};

interface DisponibilitePlacesProps {
  session: SessionPlaces;
  className?: string;
}

/** « 5 places disponibles (sur 12) » avec la jauge de remplissage juste en dessous. */
const DisponibilitePlaces = ({ session, className }: DisponibilitePlacesProps) => {
  const l = libellePlaces(session);
  const Icone = ICONES[l.niveau];
  const style = STYLES[l.niveau];

  return (
    <div className={className}>
      <p className={clsx('flex items-center gap-1.5 text-sm font-medium', style.texte)}>
        <Icone className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>{l.texte}</span>
        {l.niveau !== 'complet' && l.capacite > 0 && (
          <span className="text-xs font-normal text-gray-400">(sur {l.capacite})</span>
        )}
      </p>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={l.capacite}
        aria-valuenow={l.capacite - l.restantes}
        aria-valuetext={l.accessible}
        aria-label="Remplissage du stage"
        className="mt-1.5 h-1.5 w-full max-w-[12rem] overflow-hidden rounded-full bg-gray-200"
      >
        <div
          className={clsx('h-full rounded-full', style.jauge)}
          style={{ width: `${Math.round(l.remplissage * 100)}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Badge court posé sur l'image d'une carte. Rien quand il reste de la place :
 * un badge vert sur chaque carte serait du bruit.
 */
export const BadgePlaces = ({ session }: { session: SessionPlaces }) => {
  const l = libellePlaces(session);
  if (l.niveau === 'disponible') return null;
  const Icone = ICONES[l.niveau];
  return (
    <div
      className={clsx(
        'absolute top-2 right-2 flex items-center gap-1 text-white text-xs font-bold px-2 py-1 rounded-full',
        STYLES[l.niveau].badge
      )}
    >
      <Icone className="h-3 w-3" aria-hidden="true" />
      {l.court}
    </div>
  );
};

export default DisponibilitePlaces;
